import { useEffect, useSyncExternalStore } from 'react';

import { getEnv } from '@/env';
import { WorkspaceOpenIMRuntime } from '@/im/openim-runtime';
import type { IWorkspaceIMBridge, WorkspaceIMSnapshot } from '@/im/openim-types';

interface RuntimeEntry {
  references: number;
  releaseTimer?: number;
  runtime: WorkspaceOpenIMRuntime;
}

const runtimeEntries = new Map<string, RuntimeEntry>();
const runtimeRegistryListeners = new Set<() => void>();
let runtimeTransition: Promise<void> = Promise.resolve();

/** subscribeRuntimeRegistry 让 React 在 Runtime 身份或引用发生变化时重新读取 Bridge。 */
function subscribeRuntimeRegistry(listener: () => void): () => void {
  runtimeRegistryListeners.add(listener);
  return () => runtimeRegistryListeners.delete(listener);
}

/** notifyRuntimeRegistry 通知所有 Host 消费者重新读取当前身份对应的 Bridge。 */
function notifyRuntimeRegistry(): void {
  runtimeRegistryListeners.forEach(listener => listener());
}

/** getRuntimeBridge 只返回当前身份仍在注册表中的 Bridge，切租户首帧不会泄漏旧状态。 */
function getRuntimeBridge(key: string): IWorkspaceIMBridge | undefined {
  return key ? runtimeEntries.get(key)?.runtime.bridge : undefined;
}

/** continueRuntimeTransition 串行等待 SDK 销毁与下一身份登录，避免全局 SDK 被旧 logout 干扰。 */
function continueRuntimeTransition(
  disposals: Promise<void>[],
  start?: () => Promise<void>,
): void {
  runtimeTransition = Promise.all([
    runtimeTransition.catch(() => undefined),
    ...disposals.map(disposal => disposal.catch(() => undefined)),
  ]).then(async () => {
    await start?.();
  });
}

/** getRuntimeKey 用员工和租户身份隔离 Runtime，地址变化也必须创建新连接。 */
function getRuntimeKey(
  tenantID: string,
  userID: string,
  apiAddr: string,
  wsAddr: string,
): string {
  return [tenantID, userID, apiAddr, wsAddr].join('|');
}

/** acquireRuntime 复用同身份单例，并取消 StrictMode 紧邻重挂载触发的延迟销毁。 */
function acquireRuntime(
  key: string,
  createRuntime: () => WorkspaceOpenIMRuntime,
): WorkspaceOpenIMRuntime {
  const existing = runtimeEntries.get(key);
  if (existing) {
    if (existing.releaseTimer) window.clearTimeout(existing.releaseTimer);
    existing.releaseTimer = undefined;
    existing.references += 1;
    return existing.runtime;
  }
  // OpenIM Web SDK 本身是进程级单例；身份切换必须先使旧世代失效并完成 logout。
  const staleEntries = [...runtimeEntries.values()];
  runtimeEntries.clear();
  staleEntries.forEach((entry) => {
    if (entry.releaseTimer) window.clearTimeout(entry.releaseTimer);
  });
  const disposals = staleEntries.map(entry => entry.runtime.dispose());
  const entry: RuntimeEntry = { references: 1, runtime: createRuntime() };
  runtimeEntries.set(key, entry);
  notifyRuntimeRegistry();
  continueRuntimeTransition(disposals, async () => {
    if (runtimeEntries.get(key) === entry && entry.references > 0) {
      await entry.runtime.start();
    }
  });
  return entry.runtime;
}

/** releaseRuntime 延迟一个任务释放单例，兼容 React StrictMode 的 effect 重放。 */
function releaseRuntime(key: string, runtime: WorkspaceOpenIMRuntime): void {
  const entry = runtimeEntries.get(key);
  if (entry?.runtime !== runtime) return;
  entry.references = Math.max(0, entry.references - 1);
  if (entry.references > 0) return;
  entry.releaseTimer = window.setTimeout(() => {
    const current = runtimeEntries.get(key);
    if (current?.runtime !== runtime || current.references > 0) return;
    runtimeEntries.delete(key);
    notifyRuntimeRegistry();
    continueRuntimeTransition([runtime.dispose()]);
  }, 0);
}

/** useWorkspaceOpenIMBridge 在 Host 生命周期内按租户员工身份获取唯一 Bridge。 */
export function useWorkspaceOpenIMBridge(
  tenantID: string,
  userID: string,
): IWorkspaceIMBridge | undefined {
  const { workspaceNotice } = getEnv();
  const key = tenantID && userID
    ? getRuntimeKey(tenantID, userID, workspaceNotice.apiAddr, workspaceNotice.wsAddr)
    : '';
  const bridge = useSyncExternalStore(
    subscribeRuntimeRegistry,
    () => getRuntimeBridge(key),
    () => undefined,
  );

  useEffect(() => {
    if (!key) return;
    const runtime = acquireRuntime(key, () => new WorkspaceOpenIMRuntime({
      apiAddr: workspaceNotice.apiAddr,
      tenantID,
      userID,
      wsAddr: workspaceNotice.wsAddr,
    }));

    return () => releaseRuntime(key, runtime);
  }, [key, tenantID, userID, workspaceNotice.apiAddr, workspaceNotice.wsAddr]);

  return bridge;
}

/** useWorkspaceIMSnapshot 订阅 Host badge 使用的最小运行时快照。 */
export function useWorkspaceIMSnapshot(
  bridge: IWorkspaceIMBridge | undefined,
): WorkspaceIMSnapshot | undefined {
  return useSyncExternalStore(
    bridge?.subscribe ?? (() => () => undefined),
    bridge?.getSnapshot ?? (() => undefined),
    bridge?.getSnapshot ?? (() => undefined),
  );
}
